import {z} from 'zod';
import {AuctionType, Encoding, hasAnyFormat, IBidder, Platform, PlacementFormatsTable} from './Bidder';

export type BidderFormValues = Omit<IBidder, 'wseat' | 'bseat' | 'minBidFloor' | 'maxBidFloor' | 'impExpirationTime' | 'mockBidResponse' | 'healthyAvgPrice' | 'encoding'> & {
    wseat: string;
    bseat: string;
    encoding: Encoding | null;
    minBidFloor: number | string | null;
    maxBidFloor: number | string | null;
    impExpirationTime: number | string | null;
    mockBidResponse: string | null;
    healthyAvgPrice: number | string | null;
};

const asNumber = (value: unknown): unknown => {
    if (value === null || value === undefined || value === '') {
        return value;
    }
    if (typeof value === 'number') {
        return value;
    }
    if (typeof value === 'string') {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : value;
    }
    return value;
};

const requiredNumber = ({max}: {max?: number} = {}) => {
    let schema = z.number({required_error: 'is required', invalid_type_error: 'is required'})
        .min(0, 'must be greater than or equal to 0');
    if (typeof max === 'number') {
        schema = schema.max(max, `must be less than or equal to ${max}`);
    }
    return z.preprocess(asNumber, schema);
};

const nullableNumber = z.preprocess((value: unknown) => {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    return asNumber(value);
}, z.number({invalid_type_error: 'you must specify a number'}).nullable());

const nullablePositiveNumber = z.preprocess((value: unknown) => {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    return asNumber(value);
}, z.number({invalid_type_error: 'you must specify a number'}).gt(0, 'must be greater than 0').nullable());

const requiredString = z.string().trim().min(1, 'is required');

const enforceSpendCapping = z.preprocess(asNumber, z.number({required_error: 'is required', invalid_type_error: 'is required'})
    .gte(100, 'must be at least $100'));

const bidderSpendCappingSchema = z.object({
    id: z.number().nullable(),
    limit: enforceSpendCapping,
    test: z.boolean().nullable(),
});

export const createBidderFormSchema = (isAdmin: boolean) => z.object({
    check: z.boolean().nullable(),
    id: z.number().nullable(),
    agencyId: z.preprocess(asNumber, z.number({required_error: 'is required', invalid_type_error: 'is required'})),
    title: requiredString,
    tags: z.array(z.string()).nullable(),
    endpoint: requiredString,
    coppaFlag: z.boolean(),
    excludeFromNonInBannerAuction: z.boolean(),
    countries: z.array(z.string()).optional(),
    platforms: z.array(z.nativeEnum(Platform)),
    maxRpm: isAdmin ? requiredNumber() : requiredNumber({max: 2500}),
    adControl: z.boolean(),
    includedSellers: z.array(z.number()),
    protocol: z.string(),
    encoding: z.nativeEnum(Encoding).nullable(),
    auctionType: z.nativeEnum(AuctionType).optional(),
    delayedNotification: z.boolean(),
    wseat: z.string(),
    bseat: z.string(),
    minBidFloor: nullablePositiveNumber,
    maxBidFloor: nullablePositiveNumber,
    floorType: requiredString,
    noFloor: z.boolean().nullable(),
    skAnIdCheck: z.boolean().nullable(),
    skAnId: z.string().nullable(),
    dc: z.array(z.string()).nullable(),
    spendCapping: bidderSpendCappingSchema,
    impExpirationTime: nullableNumber,
    status: z.string(),
    mockBidResponse: z.string().nullable(),
    useAdMergeType: z.boolean().nullable(),
    auctionCategory: z.enum(['performance', 'branded']),
    clientNurl: z.boolean(),
    clientLurl: z.boolean(),
    passCalloutRound: z.boolean().default(false),
    tagIdRequirement: z.enum(['no_filtering', 'original_only', 'any_non_empty_tag_id']).default('no_filtering'),
    gpidRequirement: z.enum(['no_filtering', 'original_only', 'any_non_empty_gpid']).default('no_filtering'),
    healthyAvgPrice: nullablePositiveNumber,
    placementFormats: z.custom<PlacementFormatsTable>().nullable()
        .refine(table => table === null || table === undefined || hasAnyFormat(table),
            'Tick at least one format, or click "Clear table (legacy rules)" to keep the bidder on legacy rules'),
    multiFormatRequest: z.boolean().default(false),
});
